import {
  BookOpen,
  BriefcaseBusiness,
  FileText,
  Presentation,
  Settings,
  Workflow,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import BackwardsHero from '../../components/backwards/BackwardsHero'
import CulturePrinciples from '../../components/backwards/CulturePrinciples'
import DoDontRows from '../../components/backwards/DoDontRows'
import FaqAccordion from '../../components/backwards/FaqAccordion'
import LearningObjectivesCard from '../../components/backwards/LearningObjectivesCard'
import MethodBlock from '../../components/backwards/MethodBlock'
import OpportunityStepper from '../../components/backwards/OpportunityStepper'
import PlaybookTopBar from '../../components/backwards/PlaybookTopBar'
import PressReleaseCard from '../../components/backwards/PressReleaseCard'
import ProblemCards from '../../components/backwards/ProblemCards'
import ProductAccordion from '../../components/backwards/ProductAccordion'
import PromiseColumns from '../../components/backwards/PromiseColumns'
import SectionFooterNav from '../../components/backwards/SectionFooterNav'
import SectionHeader from '../../components/backwards/SectionHeader'
import SectionIndex from '../../components/backwards/SectionIndex'
import SegmentedTabs from '../../components/backwards/SegmentedTabs'
import ServicesComparisonTable from '../../components/backwards/ServicesComparisonTable'
import SuccessMetricsList from '../../components/backwards/SuccessMetricsList'
import SupportMaterialCard from '../../components/backwards/SupportMaterialCard'
import TwoColumnStatement from '../../components/backwards/TwoColumnStatement'
import ValuePillarsCard from '../../components/backwards/ValuePillarsCard'
import ValuePropStats from '../../components/backwards/ValuePropStats'
import { omnicoreTheme } from '../../components/backwards/playbookThemes'
import { cn } from '../../utils/cn'

const PLACEHOLDER = '— conteúdo a definir —'

// Citação lateral dos cabeçalhos: Inter ExtraLight 200 itálico (peso exato do
// Figma; a fonte foi adicionada no M0).
const QUOTE_CLASS =
  'font-inter-extralight text-[28px] italic leading-[1.08] tracking-[-0.02em] text-[#454545]'

// REVISAR: objetivos redigidos por inferência (o widget não existe nos frames
// da Omnicore; incluído por decisão D5 para paridade com o playbook Nex2AI).
const learningObjectives = [
  'Qual é o posicionamento da Omnicore',
  'Quais problemas de ERP a Omnicore resolve',
  'Como cada solução se conecta à jornada do cliente',
  'Como explicar a proposta de valor da empresa',
  'Como a cultura Omnicore orienta cada oportunidade',
]

const indexItems = [
  {
    number: '01',
    title: 'Como você vai aprender',
    description:
      'A jornada foi estruturada para explicar primeiro os desafios do mercado, depois como a Omnicore resolve esses problemas e, por fim, como comunicar esse valor durante todo o relacionamento com o cliente.',
  },
  {
    number: '02',
    title: 'Conhecendo a Omnicore',
    description:
      'Entenda nossa missão, diferenciais, cultura, perfil de clientes e a forma como transformamos tecnologia de gestão em resultados para o negócio.',
  },
  {
    number: '03',
    title: 'Nossos Serviços',
    description:
      'Conheça cada solução, os problemas que ela resolve, quando indicá-la e como explicar seu valor de forma clara e prática.',
  },
]

const methodBody =
  'O método **Working Backwards** foi escolhido como estrutura de aprendizado dos serviços da Omnicore porque facilita a compreensão da narrativa comercial antes dos detalhes operacionais da solução.\n\nÉ um framework criado pela Amazon onde você começa imaginando o resultado final (como se estivesse anunciando o serviço para o mercado) e trabalha de trás para frente. Ao invés de começar pela tecnologia, você começa pelo problema que resolve e o valor que entrega.\n\nIsso ajuda o time comercial a desenvolver uma comunicação mais clara, consultiva e orientada a valor, padronizando a forma como a empresa comunica ao mercado.\n\nO Objetivo é acelerar o entendimento comercial. Não aprofundar tecnicamente.'

const methodBullets = [
  'Qual problema o serviço resolve de fato',
  'Como apresentar de forma simples, estratégica e executiva',
  'Como contextualizar a solução comercialmente',
  'Para quem ele faz sentido e por que',
  'Quais dúvidas vão surgir e como respondê-las com segurança',
]

const pressReleaseIntro = [
  'O **Press Release** é uma forma estruturada de apresentar um serviço pela ótica do impacto e da transformação que ele gera para o cliente, em um formato de notícia, como se estivéssemos comunicando o lançamento de um novo produto em veículos de comunicação, por exemplo.',
  'O objetivo não é decorar textos, mas entender como o serviço deve ser apresentado comercialmente.',
]

const pressReleaseBlocks = [
  {
    type: 'p',
    text: 'Empresas que utilizam sistemas como SAP, TOTVS, Senior e outros ERPs agora contam com uma abordagem especializada para eliminar gargalos operacionais, acelerar atendimentos e extrair mais valor dos seus sistemas de gestão.',
  },
  {
    type: 'quote',
    text: '"A tecnologia deve impulsionar o negócio, não limitar sua operação. Nosso papel é transformar sistemas complexos em ferramentas que geram eficiência, previsibilidade e resultados reais."',
    author: 'Head Comercial, Omnicore',
  },
  {
    type: 'p',
    text: 'A Omnicore atua ao lado das empresas para compreender o problema real antes de propor qualquer solução. Combinando conhecimento técnico, visão de negócio e uma abordagem focada em quick-wins, a empresa reduz burocracias, acelera a resolução de problemas e garante maior eficiência na sustentação e evolução dos sistemas de gestão.',
  },
  {
    type: 'p',
    text: 'Mais do que prestar suporte, a Omnicore conecta estratégia, processos e tecnologia para manter a operação funcionando com estabilidade, apoiar decisões de negócio e permitir que as empresas obtenham o máximo retorno dos investimentos realizados em seus ERPs.',
  },
]

const faqIntro =
  'Durante o estudo dos nossos serviços, você encontrará uma seção de FAQ criada para apoiar reuniões comerciais, apresentações e conversas estratégicas. Ela reúne as principais dúvidas e objeções que costumam surgir durante a jornada do cliente.'

const faqCallout =
  'Ler a seção de **FAQ** de cada serviço ajudará você a comunicar valor com mais segurança, responder às principais objeções e conduzir conversas de forma clara, sem precisar aprofundar tecnicamente cada solução.'

const faqItems = [
  {
    question: 'O que exatamente a Omnicore faz?',
    answer:
      'A Omnicore é uma consultoria especializada em sustentação, evolução e projetos de sistemas de gestão (ERPs). Atuamos para eliminar gargalos operacionais, acelerar atendimentos, evoluir processos e garantir que a tecnologia gere resultados para o negócio.',
  },
  { question: 'Isso serve para a minha empresa?', answer: PLACEHOLDER },
  {
    question: 'Como a Omnicore se diferencia de outras consultorias?',
    answer: PLACEHOLDER,
  },
  { question: 'Quais resultados posso esperar?', answer: PLACEHOLDER },
]

const problemLead =
  'A maioria das empresas já investiu em um ERP para controlar sua operação, mas continua enfrentando desafios que comprometem produtividade, crescimento e resultados. Na prática, esses problemas normalmente se concentram em três grandes frentes.'

const problemCards = [
  {
    tag: 'Problema 1',
    tagColor: '#CA3500',
    title: 'O ERP não acompanha a operação.',
    text: 'O sistema foi implantado, mas processos continuam lentos, manuais ou dependentes de planilhas. A operação cresce, mas o ERP não evolui junto.',
    solution: {
      product: 'Projetos e Evolução de ERP',
      text: 'Desenvolvimento, customizações e melhorias que adaptam o ERP às necessidades reais da empresa.',
    },
  },
  {
    tag: 'Problema 2',
    tagColor: '#CA3500',
    title: 'O suporte demora e a operação para.',
    text: 'Chamados ficam dias aguardando atendimento, problemas simples se tornam críticos e diferentes fornecedores transferem a responsabilidade entre si.',
    solution: {
      product: 'AMS / AMS Flex',
      text: 'Sustentação especializada, atendimento ágil e governança centralizada para manter a operação funcionando.',
    },
  },
  {
    tag: 'Problema 3',
    tagColor: '#CA3500',
    title: 'A empresa perdeu o controle da tecnologia.',
    text: 'Projetos atrasados, riscos de auditoria, excesso de acessos, desperdício de licenças e decisões tomadas sem informações confiáveis.',
    solution: {
      product: 'ERP Advisorship / GRC SAP / Recuperação de Projetos',
      text: 'Consultoria estratégica para recuperar projetos, fortalecer a governança e apoiar decisões com segurança.',
    },
  },
]

const comparisonLead =
  'Não pense nos serviços da Omnicore como soluções isoladas. Cada um atende um momento diferente da jornada do cliente e pode ser combinado conforme a necessidade do negócio.'

const comparisonColumns = [
  'Serviço',
  'Pergunta que responde',
  'O que entrega na prática',
  'Para quem',
]

const comparisonRows = [
  [
    'AMS / AMS Flex',
    'Como manter minha operação funcionando?',
    'Sustentação especializada e evolução contínua dos ERPs.',
    'Empresas que precisam de estabilidade operacional.',
  ],
  [
    'ERP Advisorship',
    'Estou tomando a decisão certa?',
    'Diagnóstico estratégico e direcionamento para evolução do ERP.',
    'Executivos e gestores de TI.',
  ],
  [
    'Recuperação de Projetos',
    'Como recuperar um projeto que saiu do controle?',
    'Reestruturação de projetos críticos com foco em resultados rápidos.',
    'Empresas com projetos atrasados ou em risco.',
  ],
  [
    'Implementação de Projetos',
    'Como evoluir meu ERP com segurança?',
    'Implantações, customizações e novas funcionalidades.',
    'Empresas em crescimento ou transformação.',
  ],
  [
    'GRC SAP',
    'Como reduzir riscos e atender auditorias?',
    'Governança, controles e conformidade.',
    'Empresas com alta exigência regulatória.',
  ],
  [
    'FUESAP',
    'Como acelerar meu faturamento?',
    'Automação inteligente do faturamento integrado ao SAP.',
    'Empresas com alto volume de notas fiscais.',
  ],
]

const valuePropIntro =
  '"Sua empresa já investiu em um ERP, mas isso não garante que a operação esteja funcionando da melhor forma. Muitas vezes os problemas estão no suporte, nos processos, na evolução do sistema ou na forma como ele é utilizado.\n\nA Omnicore entra justamente nesse ponto: entendemos o problema real, eliminamos gargalos, aceleramos a operação e ajudamos sua empresa a extrair mais valor do ERP com rapidez, transparência e foco em resultados."'

const pillarsNoteTitle =
  'Nota importante: A Omnicore não vende apenas serviços de ERP.'

const pillarsNote =
  'Nosso maior diferencial não está apenas no conhecimento técnico sobre SAP, TOTVS, Senior ou outros sistemas de gestão.\nEstá na capacidade de compreender o contexto do cliente, identificar a causa dos problemas e conectar tecnologia, processos e pessoas para gerar resultados consistentes.'

const pillarsCallout =
  'É na integração entre tecnologia, processos e negócio que a Omnicore gera o maior impacto para seus clientes.'

const pillars = [
  {
    icon: Settings,
    title: 'Tecnologia',
    text: 'Implementamos, evoluímos e sustentamos os sistemas.',
  },
  {
    icon: BriefcaseBusiness,
    title: 'Negócio',
    text: 'Garantimos que cada iniciativa gere valor real para a empresa.',
  },
  {
    icon: Workflow,
    title: 'Processos',
    text: 'Eliminamos gargalos e aumentamos a eficiência operacional.',
  },
]

const serviceTabsConfig = [
  { id: 'pressRelease', label: 'Press Release' },
  { id: 'faq', label: 'FAQ' },
  { id: 'paraQuem', label: 'Para Quem' },
]

const servicesIntro =
  'Agora que você compreendeu o posicionamento da Omnicore e os desafios que resolvemos, é hora de conhecer cada uma das nossas soluções. Nesta seção você entenderá quando cada serviço deve ser recomendado, qual problema ele resolve e como comunicar seu valor de forma simples durante uma conversa com o cliente.'

const fuesapFaq = [
  {
    question: 'O que é o FUESAP?',
    answer:
      'O FUESAP é uma solução da Omnicore integrada ao SAP que automatiza processos de faturamento em grande volume. Ela reduz atividades manuais, valida regras fiscais automaticamente e ajuda as empresas a faturarem com mais rapidez, segurança e conformidade.',
  },
  {
    question:
      'Minha empresa já utiliza o SAP. Por que eu precisaria do FUESAP?',
    answer:
      'O SAP é um sistema robusto, mas processos de faturamento com grande volume e alta complexidade fiscal costumam exigir muitas etapas manuais. O FUESAP automatiza essas atividades, reduz erros operacionais e torna o processo muito mais eficiente.',
  },
  {
    question: 'A implantação pode interromper minha operação?',
    answer:
      'Não. A implantação é planejada para minimizar impactos na rotina da empresa. Antes da entrada em produção, realizamos o mapeamento do processo, testes em ambiente homologado e um plano de transição para garantir a continuidade da operação.',
  },
  {
    question: 'Para quais empresas o FUESAP é indicado?',
    answer:
      'É indicado para empresas de médio e grande porte que possuem alto volume de faturamento, principalmente nos segmentos de varejo, distribuição, e-commerce e farmacêutico.',
  },
  {
    question: 'Quais problemas o FUESAP resolve?',
    answer:
      '- Reduz processos manuais no faturamento.\n- Minimiza erros de emissão de notas fiscais.\n- Automatiza validações tributárias.\n- Aumenta a velocidade de processamento.\n- Garante maior conformidade fiscal.\n- Ajuda a empresa a lidar com períodos de alta demanda sem comprometer a operação.',
  },
  {
    question: 'Como saber se minha empresa precisa dessa solução?',
    answer:
      'Alguns sinais comuns são:\n- A equipe leva muito tempo para concluir o faturamento diário.\n- Há muitas notas fiscais rejeitadas por erros operacionais.\n- O faturamento depende de processos manuais.\n- A empresa enfrenta dificuldades para atender picos de demanda, como fechamentos de mês ou Black Friday.',
  },
  {
    question: 'Qual é o principal benefício do FUESAP?',
    answer:
      'Permitir que a empresa processe grandes volumes de faturamento com mais rapidez, segurança e previsibilidade, reduzindo o retrabalho da equipe e aumentando a eficiência operacional.',
  },
]

const grcFaq = [
  {
    question: 'O que é GRC SAP?',
    answer:
      'GRC é a sigla para Governance, Risk and Compliance (Governança, Riscos e Conformidade). No SAP, ele é um conjunto de recursos que ajuda a empresa a controlar quem pode acessar determinadas informações, evitar conflitos de permissões, reduzir riscos operacionais e garantir que os processos estejam em conformidade com políticas internas e exigências de auditoria. Em vez de descobrir um problema depois que ele aconteceu, o GRC ajuda a prevenir que ele aconteça.',
  },
  {
    question:
      'Nossa empresa já possui auditoria externa. Por que contratar uma consultoria de GRC SAP?',
    answer:
      'A auditoria normalmente identifica problemas depois que eles já aconteceram. O trabalho da Omnicore é diferente. Configuramos o GRC SAP para prevenir esses problemas, traduzindo as regras de auditoria e compliance em controles automáticos dentro do sistema.',
  },
  {
    question:
      'Implementar GRC SAP vai deixar nossos processos mais burocráticos?',
    answer:
      'Não. O objetivo é justamente reduzir atividades manuais. Em vez de depender de planilhas, aprovações paralelas e conferências constantes, o GRC automatiza controles e validações, permitindo que apenas situações realmente críticas sejam analisadas pela equipe.',
  },
  {
    question: 'O GRC serve apenas para atender auditorias?',
    answer:
      'Não. Embora facilite auditorias, seu principal benefício é proteger a operação diariamente. Ele ajuda a controlar acessos, evitar conflitos de funções, reduzir riscos de fraude e aumentar a segurança dos processos executados no SAP.',
  },
  {
    question:
      'Nossa empresa nunca sofreu uma fraude. Ainda assim faz sentido investir em GRC?',
    answer:
      'Sim. O objetivo do GRC é atuar de forma preventiva. Ele identifica riscos antes que eles se transformem em perdas financeiras, problemas de conformidade ou apontamentos em auditorias.',
  },
  {
    question: 'O projeto exige mudanças na forma como os usuários trabalham?',
    answer:
      'Nem sempre. Grande parte das melhorias acontece na configuração dos controles e das permissões do SAP. Quando ajustes de processo são necessários, eles são realizados de forma planejada para manter a operação funcionando normalmente.',
  },
  {
    question: 'Como a Omnicore define quais controles devem ser implementados?',
    answer:
      'A equipe realiza uma análise do ambiente atual, revisa os riscos existentes, avalia as permissões de acesso e define, junto com o cliente, quais controles oferecem maior proteção para o negócio e melhor aderência às políticas da empresa. Dessa forma, a solução atende às necessidades reais da organização sem criar burocracias desnecessárias.',
  },
]

const amsFaq = [
  {
    question: 'O que é AMS?',
    answer:
      'AMS (Application Management Services) é um serviço especializado de sustentação e evolução contínua do ERP. Em vez de contratar consultorias para cada necessidade, a empresa conta com uma equipe especializada para atender incidentes, realizar correções, desenvolver pequenas evoluções e manter o sistema funcionando de forma estável ao longo do tempo.',
  },
  {
    question: 'Qual a diferença entre AMS e Flex AMS?',
    answer:
      'Ambos têm o objetivo de garantir a sustentação do ERP. O Flex AMS oferece maior flexibilidade na utilização das horas contratadas, permitindo que elas sejam utilizadas tanto para o suporte do dia a dia quanto para demandas de evolução e projetos, conforme a necessidade da empresa.',
  },
  {
    question:
      'Minha equipe interna já cuida do ERP. Ainda faz sentido contratar AMS?',
    answer:
      'Sim. O AMS atua como uma extensão da equipe interna, oferecendo especialistas para atender demandas técnicas e funcionais, reduzir a sobrecarga da equipe e acelerar a resolução de problemas sem que a empresa precise aumentar seu quadro de colaboradores.',
  },
  {
    question: 'O AMS serve apenas para corrigir erros?',
    answer:
      'Não. Além de atender incidentes em produção, o serviço também contempla customizações, desenvolvimento de relatórios, integrações e pequenas evoluções que ajudam o ERP a acompanhar as mudanças do negócio.',
  },
  {
    question: 'Posso utilizar o AMS mesmo tendo mais de um ERP na empresa?',
    answer:
      'Sim. O serviço foi estruturado para que empresas possam centralizar o suporte de um ou múltiplos ERPs em um único contrato, simplificando a gestão e o relacionamento com o fornecedor.',
  },
  {
    question: 'Quando o AMS é mais indicado do que um projeto de implantação?',
    answer:
      'O AMS é indicado quando o ERP já está em operação e a empresa precisa garantir estabilidade, suporte especializado e evolução contínua. Já os projetos são recomendados quando há necessidade de implantar novos módulos, realizar grandes customizações ou executar mudanças estruturais no sistema.',
  },
]

const advisorshipFaq = [
  {
    question: 'O que é ERP Advisorship?',
    answer:
      'ERP Advisorship é um serviço de consultoria estratégica e independente que ajuda a empresa a avaliar a situação atual do seu ERP antes de tomar decisões importantes. Em vez de recomendar uma implementação imediatamente, a Omnicore realiza um diagnóstico completo do ambiente, identifica riscos, revisa contratos e apresenta um plano de ação para que a empresa invista apenas no que realmente gera valor.',
  },
  {
    question:
      'Quanto tempo dura esse diagnóstico e o que a empresa recebe ao final?',
    answer:
      'O ciclo de avaliação normalmente leva entre 4 e 6 semanas. Ao final, a empresa recebe um Relatório Executivo Consolidado contendo os riscos identificados, uma auditoria do uso das licenças e um plano de ação organizado em curto, médio e longo prazo.',
  },
  {
    // REVISAR: no Figma esta pergunta é uma sobra do FAQ de AMS ("Minha equipe
    // interna já cuida do ERP..."). Substituída por inferência.
    question: 'O diagnóstico atrapalha a rotina da minha equipe de TI?',
    answer:
      'Não. O trabalho é conduzido pelos especialistas da Omnicore com entrevistas pontuais e análise do ambiente, contratos e configurações existentes. A rotina da equipe interna é preservada e o envolvimento dela se concentra em validações e alinhamentos estratégicos.',
  },
  {
    question: 'O consultor também implementa as melhorias identificadas?',
    answer:
      'Não nesta etapa. O papel do ERP Advisorship é atuar como auditor e conselheiro estratégico, oferecendo uma visão técnica e imparcial para orientar as decisões da empresa.',
  },
  {
    question:
      'Qual a diferença entre o ERP Advisorship e uma consultoria tradicional de ERP?',
    answer:
      'Enquanto muitas consultorias são contratadas para implementar soluções, o ERP Advisorship tem como objetivo avaliar o cenário atual de forma independente. A prioridade é entender a realidade da empresa, identificar riscos e orientar os investimentos antes de qualquer decisão sobre novos projetos ou migrações. Essa neutralidade permite que as decisões sejam tomadas com base em fatos e não apenas em propostas comerciais.',
  },
  {
    question:
      'O ERP Advisorship é indicado apenas para empresas que vão trocar de ERP?',
    answer:
      'Não. O serviço também é indicado para empresas que desejam entender por que o ERP atual não está entregando os resultados esperados, revisar contratos de licenciamento, auditar projetos em andamento ou avaliar a qualidade do trabalho realizado por fornecedores.',
  },
]

const recuperacaoFaq = [
  {
    question: 'O que é Recuperação de Projetos de ERP?',
    answer:
      'É um serviço especializado para empresas que possuem projetos de ERP com atrasos, dificuldades de execução ou baixa qualidade nas entregas. A Omnicore analisa o cenário atual, identifica os principais problemas e estrutura um plano para retomar o projeto de forma organizada e segura.',
  },
  {
    question: 'Meu projeto está atrasado. Ainda é possível recuperá-lo?',
    answer:
      'Sim. O objetivo do serviço é justamente identificar as causas dos atrasos e definir um plano de ação que permita reorganizar o projeto, priorizar as entregas críticas e conduzir a implantação até a conclusão.',
  },
  {
    question:
      'Vocês conseguem assumir um projeto iniciado por outra consultoria?',
    answer:
      'Sim. A Omnicore realiza uma avaliação técnica do ambiente existente, revisa o trabalho já executado e define a melhor estratégia para dar continuidade ao projeto, independentemente de quem iniciou a implantação.',
  },
  {
    question: 'Será necessário começar o projeto do zero?',
    answer:
      'Nem sempre. Antes de qualquer decisão, a Omnicore analisa tudo o que já foi desenvolvido para identificar o que pode ser aproveitado e o que realmente precisa ser refeito. O objetivo é preservar os investimentos já realizados sempre que isso for tecnicamente viável.',
  },
  {
    question: 'Como saber se meu projeto precisa de uma recuperação?',
    answer:
      'Alguns sinais comuns são atrasos constantes, orçamento esgotado antes da conclusão, falta de previsibilidade para o Go-Live, conflitos entre as equipes envolvidas e entregas que não atendem às necessidades da operação.',
  },
  {
    question: 'A Omnicore substitui a consultoria atual?',
    answer:
      'Depende da situação encontrada durante a avaliação. O serviço pode apoiar a recuperação do projeto em conjunto com a equipe existente ou assumir a condução das próximas etapas, sempre buscando a alternativa que ofereça menor risco para a empresa.',
  },
]

const implementacaoFaq = [
  {
    question: 'O que é o serviço de Implementação de Novos Projetos?',
    answer:
      'É um serviço especializado para empresas que desejam implantar um novo ERP ou realizar grandes evoluções em um sistema já existente. A Omnicore conduz o projeto desde o desenho da solução até a implantação, incluindo desenvolvimento, integrações, migração de dados e treinamento das equipes.',
  },
  {
    question:
      'Meu ERP já está funcionando. Ainda assim faz sentido contratar esse serviço?',
    answer:
      'Sim. O serviço também atende empresas que precisam ampliar as funcionalidades do ERP, desenvolver novos módulos, criar integrações ou adaptar o sistema às mudanças do negócio, sem a necessidade de substituir toda a plataforma.',
  },
  {
    question:
      'A Omnicore desenvolve funcionalidades que não existem no ERP padrão?',
    answer:
      'Sim. O serviço contempla o desenvolvimento de rotinas personalizadas, novas funcionalidades e customizações que atendam necessidades específicas da operação da empresa quando o ERP padrão não oferece esses recursos.',
  },
  {
    question:
      'Vocês conseguem trabalhar sobre customizações feitas por outras consultorias?',
    answer:
      'Sim. A equipe da Omnicore pode analisar o ambiente existente e atuar sobre customizações previamente desenvolvidas, avaliando a melhor forma de evoluir o sistema com segurança.',
  },
  {
    question: 'O serviço inclui migração de dados e treinamento dos usuários?',
    answer:
      'Sim. Além da implantação e das customizações, o serviço contempla a migração segura dos dados e o treinamento das equipes para que a empresa possa utilizar o novo ambiente com segurança e eficiência.',
  },
  {
    question:
      'Como a Omnicore garante que o ERP acompanhe o crescimento da empresa?',
    answer:
      'Cada projeto é desenvolvido considerando as necessidades atuais e futuras do negócio. A proposta é adaptar o ERP à realidade da empresa por meio de novos módulos, integrações, relatórios estratégicos e funcionalidades personalizadas que eliminem gargalos e aumentem a eficiência operacional.',
  },
]

// Aba "Para Quem" padronizada no formato do S01 (D16): "Perfil de cliente" +
// "Quando recomendar esta solução". Nos serviços em que o Figma traz só o
// perfil, os sinais de recomendação foram inferidos do FAQ/press release.
const fuesapParaQuem = {
  sections: [
    {
      title: 'Perfil de cliente',
      intro:
        'O FUESAP é indicado para empresas de médio e grande porte que possuem alto volume de faturamento e alta complexidade fiscal, especialmente nos segmentos de:',
      items: ['Varejo', 'Distribuição', 'E-commerce', 'Farmacêutico'],
    },
    {
      title: 'Quando recomendar esta solução',
      intro:
        'O FUESAP é recomendado quando a empresa apresenta um ou mais dos seguintes sinais:',
      items: [
        'Demora excessiva ou necessidade de horas extras para concluir o faturamento diário.',
        'Alto índice de notas fiscais rejeitadas ou erros de digitação de alíquotas.',
        'Preocupação com gargalos operacionais em períodos de grande volume de vendas, como Black Friday ou fechamento de mês.',
      ],
    },
  ],
}

const grcParaQuem = {
  sections: [
    {
      title: 'Perfil de cliente',
      intro:
        'O GRC SAP é indicado para organizações que possuem processos críticos, altos requisitos de segurança e precisam garantir que seus controles internos acompanhem o crescimento do negócio. É especialmente recomendado para empresas que dependem do SAP e não podem correr riscos relacionados a acessos indevidos, fraudes ou não conformidades em auditorias.',
      items: [
        'Grandes empresas que utilizam SAP e precisam fortalecer a governança e a segurança dos seus processos.',
        'Bancos e instituições financeiras que possuem elevados requisitos de controle e conformidade.',
        'Operadoras de saúde que precisam garantir rastreabilidade, segregação de funções e conformidade em seus processos.',
        'Empresas que passam regularmente por auditorias internas e externas e precisam reduzir riscos e evitar apontamentos.',
      ],
    },
    {
      // REVISAR: sinais inferidos (o Figma só traz o perfil para este serviço).
      title: 'Quando recomendar esta solução',
      intro:
        'O GRC SAP é recomendado quando a empresa apresenta um ou mais dos seguintes sinais:',
      items: [
        'Auditorias recentes com apontamentos sobre acessos, permissões ou segregação de funções.',
        'Controles internos dependentes de planilhas, aprovações paralelas e conferências manuais.',
        'Falta de visibilidade sobre quem acessa o quê dentro do SAP.',
        'Crescimento da operação sem revisão das permissões e dos controles do ERP.',
      ],
    },
  ],
}

const amsParaQuem = {
  sections: [
    {
      title: 'Perfil de cliente',
      intro:
        'O AMS e o Flex AMS são indicados para empresas que dependem do ERP em suas operações diárias e precisam garantir suporte especializado, rapidez no atendimento e evolução contínua do sistema sem aumentar sua equipe interna. Também são uma excelente opção para organizações que desejam centralizar o atendimento de um ou mais ERPs em um único parceiro.',
      items: [
        'Empresas que utilizam SAP, TOTVS ou Senior como sistemas críticos para o negócio.',
        'Organizações que precisam de atendimento rápido para incidentes em ambiente de produção.',
        'Empresas que desejam evoluir continuamente o ERP sem abrir um novo projeto a cada demanda.',
        'Negócios que possuem uma equipe interna reduzida e precisam do apoio de especialistas.',
      ],
    },
    {
      // REVISAR: sinais inferidos (o Figma só traz o perfil para este serviço).
      title: 'Quando recomendar esta solução',
      intro:
        'O AMS / AMS Flex é recomendado quando a empresa apresenta um ou mais dos seguintes sinais:',
      items: [
        'Chamados de ERP que levam dias para serem atendidos e impactam a operação.',
        'Diferentes fornecedores transferindo a responsabilidade entre si a cada incidente.',
        'Equipe interna sobrecarregada com demandas de suporte e sem tempo para evoluções.',
        'Necessidade de centralizar o suporte de múltiplos ERPs em um único contrato.',
      ],
    },
  ],
}

const advisorshipParaQuem = {
  sections: [
    {
      title: 'Perfil de cliente',
      intro:
        'O ERP Advisorship é indicado para empresas que precisam tomar decisões estratégicas sobre seus sistemas de gestão com mais segurança e embasamento técnico. O serviço atende organizações que desejam avaliar a situação atual do ERP antes de iniciar novos investimentos, migrações ou mudanças importantes, garantindo que cada decisão seja baseada em dados e não apenas em percepções.',
      items: [
        'Diretores, CEOs, CFOs e gestores de TI que estão insatisfeitos com a performance atual do ERP.',
        'Empresas que estão planejando uma migração ou modernização do sistema de gestão.',
        'Organizações que desejam auditar se a consultoria atual está conduzindo o projeto da melhor forma.',
        'Empresas que precisam revisar contratos de licenciamento e identificar oportunidades para reduzir desperdícios e otimizar investimentos.',
      ],
    },
    {
      // REVISAR: sinais inferidos (o Figma só traz o perfil para este serviço).
      title: 'Quando recomendar esta solução',
      intro:
        'O ERP Advisorship é recomendado quando a empresa apresenta um ou mais dos seguintes sinais:',
      items: [
        'Decisão importante sobre o ERP no horizonte (migração, upgrade ou troca de fornecedor).',
        'Sensação de que o ERP custa caro e entrega menos do que deveria, sem dados que comprovem.',
        'Dúvidas sobre a qualidade do trabalho da consultoria ou do fornecedor atual.',
        'Contratos de licenciamento sem revisão há vários anos.',
      ],
    },
  ],
}

const recuperacaoParaQuem = {
  sections: [
    {
      title: 'Perfil de cliente',
      intro:
        'A Recuperação de Projetos de ERP é indicada para empresas que possuem implantações em andamento ou interrompidas e precisam restabelecer o controle do projeto. O serviço ajuda organizações que enfrentam atrasos, desgaste entre equipes, baixa qualidade nas entregas ou falta de previsibilidade para concluir a implantação, permitindo retomar o projeto com uma estratégia clara e foco nos resultados.',
      items: [
        'Empresas com projetos de ERP atrasados ou sem previsão confiável de conclusão.',
        'Organizações com equipes internas sobrecarregadas, exaustas ou desmotivadas devido ao andamento do projeto.',
        'Empresas insatisfeitas com a qualidade das entregas da consultoria responsável pela implantação.',
        'Organizações que precisam recuperar o planejamento, concluir o Go-Live e restabelecer a confiança no projeto.',
      ],
    },
    {
      // REVISAR: sinais inferidos (o Figma só traz o perfil para este serviço).
      title: 'Quando recomendar esta solução',
      intro:
        'A Recuperação de Projetos é recomendada quando a empresa apresenta um ou mais dos seguintes sinais:',
      items: [
        'Cronograma do projeto estourado mais de uma vez, sem nova previsão confiável de Go-Live.',
        'Orçamento consumido antes das entregas principais serem concluídas.',
        'Conflitos recorrentes entre a equipe interna e a consultoria responsável.',
        'Entregas que não atendem às necessidades da operação e geram retrabalho.',
      ],
    },
  ],
}

const implementacaoParaQuem = {
  sections: [
    {
      title: 'Perfil de cliente',
      intro:
        'O serviço de Implementação de Novos Projetos é indicado para empresas que precisam implantar um novo ERP ou realizar evoluções importantes em seus sistemas de gestão. A solução atende organizações que desejam adaptar a tecnologia ao crescimento do negócio, eliminar processos manuais e desenvolver funcionalidades que aumentem a eficiência da operação.',
      items: [
        'Empresas que desejam implantar um novo ERP.',
        'Organizações que precisam desenvolver novos módulos ou funcionalidades para o sistema atual.',
        'Empresas que necessitam realizar grandes customizações, integrações ou migração de dados.',
        'Organizações que desejam modernizar seus processos e adaptar o ERP às novas necessidades do negócio.',
      ],
    },
    {
      // REVISAR: sinais inferidos (o Figma só traz o perfil para este serviço).
      title: 'Quando recomendar esta solução',
      intro:
        'A Implementação de Novos Projetos é recomendada quando a empresa apresenta um ou mais dos seguintes sinais:',
      items: [
        'Processos importantes do negócio ainda rodando fora do ERP, em planilhas ou sistemas paralelos.',
        'Crescimento ou mudança do negócio que o sistema atual não acompanha.',
        'Necessidade de novos módulos, integrações ou migração de dados com segurança.',
        'Decisão tomada de implantar um novo ERP e busca por um parceiro para conduzir o projeto.',
      ],
    },
  ],
}

const services = [
  {
    id: 'fuesap',
    badge: 'S01',
    title: 'FUESAP',
    tabs: serviceTabsConfig,
    contentByTab: {
      pressRelease: {
        eyebrow: 'São Paulo, 2025 — Para publicação imediata',
        headline:
          'Omnicore lança FUESAP para transformar processos de faturamento em operações mais rápidas, seguras e eficientes.',
        body: 'Empresas que lidam com grandes volumes de faturamento passam a contar com uma solução integrada ao **SAP** capaz de automatizar processos, reduzir erros operacionais e garantir **conformidade fiscal** sem aumentar a carga de trabalho das equipes.\n\nVoltado para organizações que enfrentam desafios com processos manuais e alto volume de notas fiscais, o **FUESAP** unifica e automatiza etapas críticas do faturamento, permitindo maior agilidade e previsibilidade operacional.\n\nAo eliminar atividades repetitivas, validar regras tributárias automaticamente e integrar informações em tempo real, a solução reduz retrabalho e apoia as empresas na escalabilidade de suas operações.\n\n> "Nosso objetivo é transformar processos complexos de faturamento em operações mais simples, rápidas e confiáveis, permitindo que nossos clientes concentrem seus esforços no crescimento do negócio."\n\nCom o FUESAP, a Omnicore reforça sua missão de simplificar tecnologias de gestão, conectando estratégia, processos e tecnologia para gerar resultados concretos aos seus clientes.',
      },
      faq: { faq: fuesapFaq },
      paraQuem: fuesapParaQuem,
    },
  },
  {
    id: 'grc',
    badge: 'S02',
    title: 'GRC SAP',
    tabs: serviceTabsConfig,
    contentByTab: {
      pressRelease: {
        eyebrow: 'São Paulo, 2025 — Para publicação imediata',
        headline:
          'Omnicore apresenta oferta especializada em GRC SAP para blindagem operacional e conformidade rigorosa',
        // REVISAR: o 3º parágrafo do Figma era uma sobra do press release do
        // FUESAP (falava de regras tributárias/faturamento); substituído por
        // inferência.
        body: 'De olho no aumento das exigências de compliance e auditoria no mercado corporativo, a Omnicore anunciou sua oferta especializada em **GRC SAP** (Governance, Risk and Compliance). A solução foi desenvolvida para ajudar empresas a reduzir riscos operacionais, fortalecer os controles internos e garantir que processos críticos estejam em conformidade com políticas corporativas e exigências regulatórias.\n\nCom uma equipe especializada em SAP, a Omnicore realiza a estruturação e parametrização do módulo GRC, revisando matrizes de riscos, automatizando controles internos, eliminando conflitos de segregação de funções e criando mecanismos que impedem fraudes antes que elas aconteçam. Além disso, a solução entrega relatórios preparados para auditorias e maior visibilidade sobre os acessos e permissões dentro do ERP.\n\nAo automatizar controles e validações, a solução reduz o esforço manual das equipes de compliance e prepara a empresa para auditorias internas e externas com muito mais previsibilidade.\n\n> "O objetivo é transformar processos manuais e reativos em uma estrutura preventiva, onde riscos são identificados e tratados antes de gerar impactos financeiros, operacionais ou regulatórios."',
      },
      faq: { faq: grcFaq },
      paraQuem: grcParaQuem,
    },
  },
  {
    id: 'ams',
    badge: 'S03',
    title: 'AMS / AMS Flex',
    tabs: serviceTabsConfig,
    contentByTab: {
      pressRelease: {
        eyebrow: 'São Paulo, 2025 — Para publicação imediata',
        headline:
          'Omnicore amplia oferta de AMS e Flex AMS para transformar o suporte de ERPs em uma operação estratégica',
        body: 'A Omnicore anunciou a ampliação de sua oferta de **AMS e Flex AMS**, um serviço especializado para empresas que precisam manter seus sistemas de gestão funcionando com estabilidade, rapidez e acompanhamento contínuo. A solução foi desenvolvida para organizações que dependem diariamente de seus ERPs e não podem ter a operação impactada por lentidão no atendimento ou dificuldades para evoluir o sistema.\n\nCom uma equipe de especialistas, a Omnicore oferece suporte funcional e técnico avançado para **SAP, TOTVS e Senior**, atendendo desde incidentes em produção até customizações, integrações e desenvolvimento de relatórios. Além disso, o serviço conta com um portal exclusivo para gestão dos atendimentos e permite que empresas centralizem o suporte de um ou múltiplos ERPs em um único contrato.\n\n> "O objetivo é garantir que o ERP acompanhe a evolução do negócio, reduzindo o tempo de resposta aos chamados e permitindo que as equipes internas foquem em atividades estratégicas enquanto a sustentação do ambiente permanece nas mãos de especialistas."',
      },
      faq: { faq: amsFaq },
      paraQuem: amsParaQuem,
    },
  },
  {
    id: 'advisorship',
    badge: 'S04',
    title: 'ERP Advisorship',
    tabs: serviceTabsConfig,
    contentByTab: {
      pressRelease: {
        eyebrow: 'São Paulo, 2025 — Para publicação imediata',
        headline:
          'Omnicore expande serviço de ERP Advisorship para apoiar decisões estratégicas com independência e segurança',
        body: 'A Omnicore anunciou a expansão do seu serviço de **ERP Advisorship**, uma consultoria estratégica e independente criada para apoiar empresas na tomada de decisões sobre seus sistemas de gestão. O serviço foi desenvolvido para organizações que precisam avaliar a situação atual do ERP, revisar investimentos realizados e definir os próximos passos com base em dados, sem o viés de quem está interessado em vender um projeto de implementação.\n\nDurante o trabalho, especialistas da Omnicore analisam o ambiente atual, revisam contratos de licenciamento, auditam configurações e códigos existentes e elaboram um plano de ação com recomendações técnicas e estratégicas. O objetivo é identificar riscos, reduzir desperdícios e orientar a empresa sobre onde investir para obter o maior retorno operacional.\n\n> "Com uma abordagem neutra e orientada ao negócio, o ERP Advisorship permite que diretores e gestores tomem decisões mais seguras antes de iniciar migrações, reestruturações ou novos investimentos em seus sistemas de gestão."',
      },
      faq: { faq: advisorshipFaq },
      paraQuem: advisorshipParaQuem,
    },
  },
  {
    id: 'recuperacao',
    badge: 'S05',
    title: 'Recuperação de Projetos de ERP',
    tabs: serviceTabsConfig,
    contentByTab: {
      pressRelease: {
        eyebrow: 'São Paulo, 2025 — Para publicação imediata',
        headline:
          'Omnicore lança serviço especializado em Recuperação de Projetos de ERP para empresas com implantações em risco',
        body: 'A Omnicore anunciou o lançamento do seu serviço de **Recuperação de Projetos de ERP**, criado para ajudar empresas que enfrentam atrasos, estouro de orçamento ou dificuldades na implantação de seus sistemas de gestão. A iniciativa atende organizações que já investiram tempo e recursos em seus projetos, mas ainda não conseguiram colocar o ERP em operação de forma estável.\n\nCom uma equipe especializada em **SAP, TOTVS e Senior**, a Omnicore realiza um diagnóstico técnico do projeto, identifica as causas dos atrasos, revisa o planejamento existente e define um plano de recuperação focado em restabelecer o andamento da implantação. O objetivo é recuperar a confiança no projeto, reduzir desperdícios e conduzir a empresa até um Go-Live seguro.\n\n> "A proposta é transformar projetos que perderam previsibilidade em iniciativas novamente controladas, permitindo que a organização volte a evoluir sem comprometer sua operação ou realizar novos investimentos sem planejamento."',
      },
      faq: { faq: recuperacaoFaq },
      paraQuem: recuperacaoParaQuem,
    },
  },
  {
    id: 'implementacao',
    badge: 'S06',
    title: 'Implementação de Novos Projetos',
    tabs: serviceTabsConfig,
    contentByTab: {
      pressRelease: {
        eyebrow: 'São Paulo, 2025 — Para publicação imediata',
        headline:
          'Omnicore amplia atuação em projetos de implantação e evolução de ERPs para acelerar a transformação das empresas',
        body: 'A Omnicore anunciou a expansão de sua oferta de **Implementação de Novos Projetos**, um serviço especializado para empresas que precisam implantar um novo ERP ou evoluir significativamente o sistema que já utilizam. A iniciativa foi criada para organizações que desejam adaptar seus processos às novas necessidades do negócio sem comprometer a continuidade da operação.\n\nCom experiência em **SAP, TOTVS e Senior**, a equipe da Omnicore atua no desenho de novos módulos, desenvolvimento de funcionalidades personalizadas, migração segura de dados, integrações e treinamento das equipes. O objetivo é garantir que o ERP acompanhe o crescimento da empresa, eliminando processos manuais, reduzindo gargalos operacionais e entregando uma solução alinhada à realidade do negócio.\n\n> "Por meio de uma abordagem estruturada e focada em resultados, a Omnicore ajuda seus clientes a transformar projetos complexos em implantações seguras, reduzindo riscos e aumentando o retorno sobre o investimento realizado em tecnologia."',
      },
      faq: { faq: implementacaoFaq },
      paraQuem: implementacaoParaQuem,
    },
  },
]

const culturePrinciples = [
  {
    title: 'Gestão Transparente',
    body: 'Comunicamos o progresso, os riscos e os resultados com clareza em todas as etapas do projeto. O cliente sempre sabe exatamente onde está e para onde está indo.',
  },
  {
    title: 'Precisão Técnica',
    body: 'Cada solução é construída com rigor técnico e conhecimento aprofundado do ERP. Não entregamos o que funciona, entregamos o que é correto.',
  },
  {
    title: 'Foco em Valor Real',
    body: 'Nossas entregas são medidas pelo impacto no negócio do cliente, não pelo número de funcionalidades implementadas. Valor é o único critério que importa.',
  },
  {
    title: 'Velocidade com Responsabilidade',
    body: 'Somos ágeis sem ser precipitados. Cada decisão rápida é sustentada por análise cuidadosa, garantindo que a velocidade não comprometa a qualidade.',
  },
]

const relacionamentoParagraphs = [
  'Nossa comunicação é direta, simples e transparente. Evitamos termos excessivamente técnicos quando eles não agregam valor e buscamos construir uma relação de parceria, não apenas de prestação de serviço.',
  'O objetivo é que o cliente compreenda o impacto das decisões sobre sua operação e participe das soluções junto conosco.',
]

const promises = {
  title: 'O QUE PROMETEMOS',
  subtitle: 'Sempre buscamos criar expectativas que realmente podemos cumprir.',
  items: [
    'Entendimento profundo da necessidade do cliente',
    'Transparência durante todo o projeto',
    'Especialistas preparados para resolver o problema correto',
    'Agilidade com responsabilidade',
    'Entregas que gerem valor para o negócio',
  ],
}

const neverPromises = {
  title: 'O QUE NUNCA PROMETEMOS',
  subtitle:
    'Existem compromissos que simplesmente não fazem parte da forma como trabalhamos.',
  items: [
    'Soluções milagrosas',
    'Prazos impossíveis apenas para fechar contratos',
    'Resultados sem compreender o cenário do cliente',
    'Atalhos que coloquem a segurança ou a qualidade em risco',
  ],
}

const opportunitySteps = [
  { title: 'Contexto', description: 'O contexto da empresa e do setor' },
  { title: 'Problema', description: 'O problema real por trás da demanda' },
  {
    title: 'Impacto',
    description: 'Os impactos para o negócio e as operações',
  },
  { title: 'Expectativas', description: 'Os critérios de sucesso do cliente' },
  {
    title: 'Solução',
    description: 'A proposta mais adequada para o problema real',
  },
]

const successMetrics = [
  'Redução do tempo de resposta',
  'Eliminação de gargalos operacionais',
  'Aumento da estabilidade do ERP',
  'Satisfação do cliente (NPS)',
  'Continuidade da operação',
]

const doDontRows = [
  {
    dont: 'Não venda funcionalidades.',
    do: 'Venda a solução para um problema.',
  },
  {
    dont: 'Não impressione com termos técnicos.',
    do: 'Explique de forma simples.',
  },
  { dont: 'Não prometa o impossível.', do: 'Construa confiança.' },
  {
    dont: 'Não tente fechar um contrato a qualquer custo.',
    do: 'Prefira perder uma venda a perder a credibilidade.',
  },
]

// Pasta oficial dos materiais comerciais da Omnicore no SharePoint
// (Marketing / 07. Comercial / 03. Omnicore).
const MATERIALS_URL =
  'https://projectivadt.sharepoint.com/sites/Corporativo/Documentos%20Compartilhados/Forms/AllItems.aspx?id=%2Fsites%2FCorporativo%2FDocumentos%20Compartilhados%2FMarketing%2F07.%20Comercial%2F03.%20Omnicore&viewid=95a3da7a-c123-4c90-a205-8595d3ef66c2&p=true&ga=1'

const scrollToSection = id => {
  const element = document.getElementById(id)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' })
  }
}

export default function BackwardsOmnicore() {
  const [section01Tab, setSection01Tab] = useState('metodologia')
  const [section02Tab, setSection02Tab] = useState('desafios')

  // Índice da capa: leva à seção Conhecendo a Omnicore já na aba certa.
  const goToConhecendo = tab => {
    setSection02Tab(tab)
    scrollToSection('secao-02')
  }

  // O header (barra navy + seta) aparece assim que o logo do hero sai da
  // tela. Uma sentinela na posição do logo dispara o IntersectionObserver.
  const [showHeader, setShowHeader] = useState(false)
  const logoSentinelRef = useRef(null)

  useEffect(() => {
    const sentinel = logoSentinelRef.current
    if (!sentinel) {
      return
    }
    const observer = new IntersectionObserver(
      ([entry]) => setShowHeader(!entry.isIntersecting),
      { threshold: 0 },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [])

  // Uma única seção "Conhecendo a Omnicore" com 3 abas (igual ao Figma).
  const conhecendoTabs = [
    { id: 'desafios', label: 'Desafios do Mercado' },
    { id: 'solucoes', label: 'Soluções Omnicore' },
    { id: 'cultura', label: 'Cultura Omnicore' },
  ]

  // O cabeçalho muda conforme a aba (D8): "Desafios" = etapa 02 (Conhecendo a
  // Omnicore); "Soluções" = etapa 03 (Nossos Serviços); "Cultura" segue o chip
  // 03 do Figma.
  const conhecendoHeader = {
    desafios: {
      number: '02',
      label: 'Conhecendo a Omnicore',
      // quebras manuais do nó do Figma (585:5947)
      title: 'O que a Omnicore faz?\nPor que o cliente deveria se\nimportar?',
      titleClass: 'whitespace-pre-line',
      quote:
        '"Antes de qualquer reunião com um cliente, você precisa conseguir responder três perguntas em 30 segundos, sem hesitar."',
    },
    solucoes: {
      number: '03',
      label: 'Nossos Serviços',
      title: 'Do Diagnóstico à Execução, da Estratégia à Evolução dos ERPs.',
      leftClass: 'md:max-w-[558px]',
      quote:
        '"Antes de qualquer reunião com um cliente, você precisa conseguir responder três perguntas em 30 segundos, sem hesitar."',
    },
    cultura: {
      number: '03',
      label: 'Conhecendo a Omnicore',
      title:
        'Mais do que entregar soluções em ERP, construímos confiança a cada relacionamento.',
      leftClass: 'md:max-w-[620px]',
      quote:
        '"A forma como conduzimos cada projeto é o que define quem somos, não apenas a tecnologia que entregamos."',
    },
  }[section02Tab]

  const renderConhecendoContent = activeTab => (
    <>
      {activeTab === 'desafios' && (
        <>
          <ProblemCards
            intro="Quais problemas a gente resolve?"
            lead={problemLead}
            cards={problemCards}
            solutionBoxClass={omnicoreTheme.solutionBox}
            solutionLabelClass={omnicoreTheme.solutionLabel}
          />
          <ServicesComparisonTable
            title="Como nossas soluções se conectam"
            lead={comparisonLead}
            columns={comparisonColumns}
            rows={comparisonRows}
            headClass={omnicoreTheme.tableHead}
          />
          <ValuePropStats
            title="Explicando a proposta de valor em ‘30s’"
            intro={valuePropIntro}
            accentBorderClass={omnicoreTheme.accentBorder}
          />
          <ValuePillarsCard
            noteTitle={pillarsNoteTitle}
            note={pillarsNote}
            pillarsLabel="Nossa atuação acontece em 3 pilares:"
            callout={pillarsCallout}
            pillars={pillars}
            accentBorderClass={omnicoreTheme.accentBorder}
            accentIconClass={omnicoreTheme.accentIcon}
          />
        </>
      )}

      {activeTab === 'solucoes' && (
        <ProductAccordion
          introHeading="Conhecendo as Soluções Omnicore"
          intro={servicesIntro}
          products={services}
          badgeClass={omnicoreTheme.badge}
          tabActiveClass={omnicoreTheme.productTabActive}
          faqQuestionClass={omnicoreTheme.faqQuestion}
          faqOpenClass={omnicoreTheme.faqOpen}
          faqIconClass={omnicoreTheme.faqIcon}
          accentBorderClass={omnicoreTheme.accentBorder}
          paraQuemTitleClass={omnicoreTheme.faqQuestion}
          paraQuemDividers
        />
      )}

      {activeTab === 'cultura' && (
        <>
          <CulturePrinciples
            title="NOSSOS PRINCÍPIOS"
            lead="Os princípios abaixo orientam todas as decisões tomadas pela Omnicore, independentemente do cliente, do projeto ou da tecnologia utilizada."
            items={culturePrinciples}
            accentBorderClass={omnicoreTheme.accentBorder}
            iconClass={omnicoreTheme.accentIcon}
          />
          <TwoColumnStatement
            heading="COMO NOS RELACIONAMOS COM NOSSOS CLIENTES"
            statement="Antes de falar sobre tecnologia, entendemos o contexto do negócio."
            paragraphs={relacionamentoParagraphs}
          />
          <PromiseColumns promises={promises} never={neverPromises} />
          <OpportunityStepper
            heading="COMO CONDUZIMOS UMA OPORTUNIDADE"
            quote='"Antes de apresentar qualquer solução, buscamos compreender o cenário completo."'
            lead="Somente depois de entender contexto, problema, impactos e expectativas apresentamos a solução mais adequada. Nosso objetivo não é vender um projeto — é resolver o problema correto."
            steps={opportunitySteps}
            closing={{
              line1: 'Nosso objetivo não é vender um projeto.',
              line2: 'É resolver o problema correto.',
            }}
          />
          <SuccessMetricsList
            heading="COMO MEDIMOS O SUCESSO"
            statement="Um projeto só é bem-sucedido quando gera resultado para o cliente."
            items={successMetrics}
          />
          <DoDontRows
            heading="COMO REPRESENTAR A OMNICORE"
            lead="Sempre que estiver diante de um cliente, lembre-se:"
            rows={doDontRows}
          />
        </>
      )}
    </>
  )

  return (
    <div className="min-h-screen w-full bg-white">
      {/* Hero + sentinela na posição do logo. Quando ela sai da tela por
          scroll, o header aparece imediatamente. */}
      <div className="relative">
        <span
          ref={logoSentinelRef}
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-[145px] h-px"
        />
        <BackwardsHero
          variant="omnicore"
          bgImage={omnicoreTheme.heroBg}
          logo={omnicoreTheme.logo}
          logoAlt={omnicoreTheme.logoAlt}
          eyebrow="Material de Apoio"
          title="Alinhamento de abordagens e apoio"
          titleHighlight="organizacional."
          subtitle="O guia para entender como a Omnicore gera valor aos seus clientes."
          subtitle2="Este playbook reúne nossa forma de atuar, nossas soluções, diferenciais e boas práticas para garantir uma atuação consistente em todos os projetos."
          badgeTitle="playbook"
          badgeText="Material de apoio para dominar o ecossistema de soluções da Omnicore."
        />
      </div>

      {/* Header fixo: desliza para o topo assim que o logo do hero sai da tela */}
      <div
        className={cn(
          'fixed inset-x-0 top-0 z-50 transition-transform duration-300',
          showHeader ? 'translate-y-0' : '-translate-y-full',
        )}
      >
        <PlaybookTopBar
          barClass={omnicoreTheme.topBar}
          logo={omnicoreTheme.logo}
          logoAlt={omnicoreTheme.logoAlt}
          logoClass="h-8"
          onBack={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        />
      </div>

      <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-10 px-20 pt-16 pb-16 lg:flex-row lg:justify-between lg:pb-[88px]">
        <div className="flex max-w-[700px] flex-col">
          <h2
            className="max-w-[460px] font-titillium-web-bold text-5xl leading-none tracking-[-0.03em] text-white lg:text-[82px]"
            style={{ WebkitTextStroke: '1px #4D4D4D' }}
          >
            Domine a Narrativa Omnicore.
          </h2>
          <p className="mt-6 whitespace-pre-line font-inter-extrabold text-3xl leading-[1.08] tracking-[-0.02em] text-[#454545] lg:mt-[41px] lg:text-[40px]">
            {'Clareza para comunicar valor\nem sistemas de gestão.'}
          </p>
          <p className="mt-6 max-w-[448px] font-inter-regular text-base leading-[1.08] tracking-[-0.02em] text-[#454545]">
            Aprenda como posicionar a Omnicore, compreender os desafios dos
            clientes e apresentar nossas soluções de forma simples, objetiva e
            conectada ao negócio.
          </p>
        </div>

        <div className="w-full lg:max-w-[562px]">
          <SectionIndex
            className="lg:px-0 lg:pt-[25px] lg:pb-0"
            titleClass="text-2xl"
            items={[
              { ...indexItems[0], onClick: () => scrollToSection('secao-01') },
              { ...indexItems[1], onClick: () => goToConhecendo('desafios') },
              { ...indexItems[2], onClick: () => goToConhecendo('solucoes') },
            ]}
          />
        </div>
      </div>

      <div className={cn('w-full overflow-hidden', omnicoreTheme.topBar)}>
        <div className="relative mx-auto flex w-full max-w-[1440px] flex-col gap-[18px] px-20 pt-[38px] pb-[39px] text-white">
          <div className="flex items-start gap-3">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center">
              <BookOpen size={24} className="text-white" />
            </span>
            <h2 className="whitespace-pre-line font-inter-black text-2xl leading-none tracking-[-0.033em] text-white">
              {
                'Templates, Apresentações e Ferramentas para\nAcelerar o Comercial.'
              }
            </h2>
          </div>
          <p className="max-w-[539px] font-inter-regular text-base leading-[1.08] text-white lg:ml-[60px]">
            Materiais desenvolvidos para apoiar reuniões, discovery,
            apresentações comerciais e o posicionamento das soluções Omnicore.
          </p>
          <img
            src="../assets/backwards/omnicore-mark.svg"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute right-[149px] top-[-31px] hidden h-[240px] w-auto lg:block"
          />
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1440px] px-20 pt-[49px] pb-16">
        <h2 className="mb-9 font-inter-black text-2xl leading-none tracking-[-0.033em] text-[#454545]">
          Materiais de Apoio Comercial
        </h2>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:gap-12">
          <SupportMaterialCard
            icon={FileText}
            status="Geral"
            title="Template de Proposta Comercial"
            description="Modelo estruturado de proposta comercial com seções padronizadas para apresentação profissional ao cliente."
            items={[
              'Resumo executivo do desafio',
              'Solução proposta e escopo',
              'Metodologia e timeline',
            ]}
            meta="Google Docs + Word"
            href={MATERIALS_URL}
            target="_blank"
            rel="noopener noreferrer"
            chipClass={omnicoreTheme.chip}
            bulletClass={omnicoreTheme.bullet}
          />
          <SupportMaterialCard
            icon={Presentation}
            status="Deck para Pitch e outras apresentações"
            title="Modelos de Apresentação"
            description="Acesse apresentações comerciais de apoio a reuniões, discovery, apresentações executivas e contextualização dos serviços."
            items={[
              'Apresentações institucionais da Omnicore',
              'Apresentações de Serviços',
              'Templates de discovery e diagnóstico',
            ]}
            meta="PowerPoint + PDF"
            href={MATERIALS_URL}
            target="_blank"
            rel="noopener noreferrer"
            chipClass={omnicoreTheme.chip}
            bulletClass={omnicoreTheme.bullet}
          />
        </div>
      </div>

      {/* SEÇÃO 01 */}
      <section id="secao-01" className="relative scroll-mt-20 pt-8">
        <div className="mx-auto w-full max-w-[1440px]">
          <SectionHeader
            number="01"
            label="Como você vai aprender?"
            eyebrow="POR QUE O MÉTODO WORKING BACKWARDS?"
            title={
              'Aprender a explicar pelo\nimpacto do serviço, não pela\ntecnologia.'
            }
            titleClass="whitespace-pre-line"
            quote="“Ao invés de começar explicando tecnologia, começamos pelo entendimento do problema, do contexto e da transformação que cada serviço gera.”"
            quoteClass={QUOTE_CLASS}
          />

          <div className="px-20 pt-[26px] pb-10">
            <SegmentedTabs
              tabs={[
                { id: 'metodologia', label: 'Metodologia' },
                { id: 'pressRelease', label: 'Press Release' },
                { id: 'faq', label: 'FAQ' },
              ]}
              active={section01Tab}
              onChange={setSection01Tab}
              activeClass={omnicoreTheme.tabActive}
            />
          </div>

          {section01Tab === 'metodologia' && (
            <MethodBlock
              intro="Sobre o método"
              body={methodBody}
              bulletsTitle="O que você passa a compreender"
              bullets={methodBullets}
              accentBorderClass={omnicoreTheme.accentBorder}
            />
          )}

          {section01Tab === 'pressRelease' && (
            <PressReleaseCard
              heading="O que é um Press Release?"
              intro={pressReleaseIntro}
              eyebrow="Exemplo de Press Release"
              headline="Omnicore transforma a sustentação e evolução de ERPs em uma operação mais ágil, transparente e orientada a resultados."
              blocks={pressReleaseBlocks}
              accentBorderClass={omnicoreTheme.accentBorder}
            />
          )}

          {section01Tab === 'faq' && (
            <FaqAccordion
              heading="Responda dúvidas e objeções com segurança"
              intro={faqIntro}
              callout={faqCallout}
              eyebrow="Exemplo de Perguntas Frequentes"
              items={faqItems}
              accentBorderClass={omnicoreTheme.accentBorder}
              iconClass={omnicoreTheme.accentIcon}
            />
          )}
        </div>
        <SectionFooterNav
          next={{ number: '02', label: 'CONHECENDO A OMNICORE' }}
          onNext={() => scrollToSection('secao-02')}
        />
      </section>

      {/* SEÇÃO 02 — Conhecendo a Omnicore (3 abas, como no Figma) */}
      <section id="secao-02" className="relative scroll-mt-20 pt-8">
        <div className="mx-auto w-full max-w-[1440px]">
          <SectionHeader
            number={conhecendoHeader.number}
            label={conhecendoHeader.label}
            title={conhecendoHeader.title}
            quote={conhecendoHeader.quote}
            quoteClass={QUOTE_CLASS}
            titleClass={conhecendoHeader.titleClass}
            leftClass={conhecendoHeader.leftClass}
          />

          <div className="px-20 pt-[26px] pb-10">
            <SegmentedTabs
              tabs={conhecendoTabs}
              active={section02Tab}
              onChange={setSection02Tab}
              activeClass={omnicoreTheme.tabActive}
            />
          </div>

          {renderConhecendoContent(section02Tab)}
        </div>
        <SectionFooterNav
          prev={{ number: '01', label: 'COMO VOCÊ VAI APRENDER' }}
          onPrev={() => scrollToSection('secao-01')}
        />
      </section>

      {/* Widget fixo: acompanha o scroll e flutua sobre o conteúdo no hover */}
      <LearningObjectivesCard objectives={learningObjectives} />
    </div>
  )
}
